import React, { useEffect, useState, useCallback } from 'react';
import { API_BASE_URL, fetchWithCsrf } from '../../../config/api';

// Admin inbox for user-submitted intake: scam reports, support tickets and
// feedback survey responses. Reports and tickets are also emailed to the
// intake mailbox; this is the durable record.

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const TABS = [
  { id: 'reports', label: 'Scam reports', kind: 'report' },
  { id: 'tickets', label: 'Support tickets', kind: 'ticket' },
  { id: 'feedback', label: 'Feedback', kind: 'feedback' },
];

// Roughly eight entries tall, then scroll — keeps the card a predictable size
// as submissions pile up instead of pushing the rest of the page down.
const LIST_SCROLL = 'max-h-[520px] overflow-y-auto pr-1';

const Entry = ({ heading, meta, body, item, onToggleResolved, busy }) => {
  const resolved = !!item?.resolved_at;
  const resolver = item?.resolved_by_name || item?.resolved_by_email;
  return (
    <div className={`py-3 border-b border-gray-100 last:border-0 ${resolved ? 'opacity-60' : ''}`}>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-medium text-black truncate">{heading}</p>
        <span className="text-[11px] text-gray-400 flex-shrink-0">{meta}</span>
      </div>
      {body && <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{body}</p>}
      <div className="flex items-center gap-3 mt-1.5">
        {resolved ? (
          <span className="text-[11px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium">
            Resolved{resolver ? ` by ${resolver}` : ''}{item.resolved_at ? ` · ${fmt(item.resolved_at)}` : ''}
          </span>
        ) : (
          <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">Open</span>
        )}
        <button
          onClick={() => onToggleResolved(item, !resolved)}
          disabled={busy}
          className="text-[11px] text-blue-600 hover:underline disabled:opacity-50"
        >
          {resolved ? 'Reopen' : 'Mark resolved'}
        </button>
      </div>
    </div>
  );
};

export default function IntakeInbox({ Section }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('reports');
  const [busyId, setBusyId] = useState(null);
  const [showResolved, setShowResolved] = useState(true);

  const load = useCallback(() => {
    fetch(`${API_BASE_URL}/bff/admin/intake?limit=50`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const activeKind = TABS.find((t) => t.id === tab)?.kind;
  const toggleResolved = async (item, resolved) => {
    setBusyId(`${activeKind}:${item.id}`);
    try {
      const r = await fetchWithCsrf(
        `${API_BASE_URL}/bff/admin/intake/${activeKind}/${item.id}/resolved`,
        { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ resolved }) }
      );
      if (r.ok) load();
    } catch { /* leave the row as-is; a reload will resync */ }
    finally { setBusyId(null); }
  };
  const visible = (rows) => (showResolved ? rows : rows.filter((r) => !r.resolved_at));

  const reports = data?.reports || [];
  const tickets = data?.tickets || [];
  const feedback = data?.feedback || [];
  const summary = data?.feedbackSummary;

  const counts = { reports: reports.length, tickets: tickets.length, feedback: feedback.length };

  return (
    <Section
      title="Intake"
      right={(
        <div className="flex gap-1">
          <button
            onClick={() => setShowResolved((v) => !v)}
            className="px-2 py-1 rounded text-xs text-gray-500 hover:bg-gray-100 mr-2"
            title={showResolved ? 'Hide resolved items' : 'Show resolved items'}
          >
            {showResolved ? 'Hide resolved' : 'Show resolved'}
          </button>
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-2 py-1 rounded text-xs ${
                tab === t.id ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {t.label} ({counts[t.id]})
            </button>
          ))}
        </div>
      )}
    >
      {tab === 'reports' && (
        reports.length === 0
          ? <p className="text-gray-400 text-sm">No scam reports yet.</p>
          : <div className={LIST_SCROLL}>{visible(reports).map((r) => (
              <Entry
                key={r.id}
                item={r}
                onToggleResolved={toggleResolved}
                busy={busyId === `report:${r.id}`}
                heading={`#${r.id} · ${r.name || 'Anonymous'}${r.email ? ` · ${r.email}` : ''}`}
                meta={fmt(r.submitted_at)}
                body={r.description}
              />
            ))}</div>
      )}

      {tab === 'tickets' && (
        tickets.length === 0
          ? <p className="text-gray-400 text-sm">No support tickets yet.</p>
          : <div className={LIST_SCROLL}>{visible(tickets).map((t) => (
              <Entry
                key={t.id}
                item={t}
                onToggleResolved={toggleResolved}
                busy={busyId === `ticket:${t.id}`}
                heading={`#${t.id} · ${t.subject || 'No subject'} — ${t.name || 'Anonymous'}${t.email ? ` · ${t.email}` : ''}`}
                meta={fmt(t.submitted_at)}
                body={t.description}
              />
            ))}</div>
      )}

      {tab === 'feedback' && (
        <>
          {summary?.total > 0 && (
            <div className="flex flex-wrap gap-6 pb-3 mb-2 border-b border-gray-200 text-sm">
              <span className="text-gray-500">Responses <b className="text-black">{summary.total}</b></span>
              <span className="text-gray-500">Awareness before <b className="text-black">{summary.avg_before ?? '—'}</b></span>
              <span className="text-gray-500">after <b className="text-black">{summary.avg_after ?? '—'}</b></span>
              <span className="text-gray-500">Effectiveness <b className="text-black">{summary.avg_effectiveness ?? '—'}</b></span>
              <span className="text-gray-500">
                Would recommend{' '}
                <b className="text-black">
                  {summary.recommend_answered ? `${summary.recommend_yes}/${summary.recommend_answered}` : '—'}
                </b>
              </span>
            </div>
          )}
          {feedback.length === 0
            ? <p className="text-gray-400 text-sm">No feedback yet.</p>
            : <div className={LIST_SCROLL}>{visible(feedback).map((f) => (
                <Entry
                  key={f.id}
                  item={f}
                  onToggleResolved={toggleResolved}
                  busy={busyId === `feedback:${f.id}`}
                  heading={`#${f.id} · before ${f.awareness_before ?? '—'} → after ${f.awareness_after ?? '—'} · effectiveness ${f.effectiveness ?? '—'} · ${
                    f.would_recommend === null ? 'no answer' : f.would_recommend ? 'would recommend' : 'would not recommend'
                  }`}
                  meta={fmt(f.submitted_at)}
                  body={f.future_release}
                />
              ))}</div>}
        </>
      )}
    </Section>
  );
}
